import { createContext, ReactNode } from 'react';
import { types as repr } from '@sitebuilder/core';
import { Annotator } from '../annotations/annotator';
import { GraphqlClientFactory } from '../graphql';
import { ILogger, Logger } from '../logger';
import { IWidgetFactory, NoOpWidgetFactory } from '../types';
import { PlaceholderProps, ViewerPlaceholder } from './Placeholder';

export interface Annotation {
    id: string;
    _id?: string;
    marker?: string;
    markerOrder?: number;
    sortOrder?: number;
    text?: string;
    markerGroup?: string;
    duplicate?: string;
    markerTargetId?: string;
    textTargetId?: string;
    inUse?: boolean;
}

export interface Annotations {
    items: Annotation[];
    annotationsIds?: string[];
    defaultMarkerGroup?: string;
    markerCount: number;
    deduplicate?: boolean;
    markerGroupCount: {
        [markerGroup: string]: { count: number };
    };
    formats: {
        [key: string]: string;
    };
}

export interface Queries {
    [key: string]: {
        isQueryLoading: boolean;
        graphqlQueryParams: repr.GraphQLQueryParams | null;
        graphQLQueryResult: repr.GraphQLQueryResult | null;
    };
}

class NoOpGraphQLClient implements repr.IGraphQLClient {
    async executeQuery<T extends repr.GraphQLQueryResult>(
        _params: repr.GraphQLQueryParams
    ): Promise<T> {
        return {} as T;
    }

    async downloadWithQuery<T extends repr.GraphQLQueryResult>(
        _params: repr.DownloadWithGraphQLQueryParams
    ): Promise<T> {
        return {} as T;
    }
}

export interface AppContextShape {
    isClient: boolean;
    data: any;
    pageName: string | null;
    componentName: string | null;
    document: repr.Document | null;
    documentType: 'pdf' | 'web' | 'pptx';
    colorPalettes: repr.Palettes | null;
    documentMetadata: repr.MetaDataOptions;
    inEditMode: boolean;
    theme: repr.Theme | null;
    themeSelected: string;
    fundList: {
        selectedList: string[];
    };
    originalHostname: string;
    baseUrl: string;
    url: string;
    queries: Queries;
    annotations: Annotations;
    annotator: Annotator;
    accessToken?: string;
    setters: {
        setSelectedList: (selectedList: string[]) => void;
        setAnnotations: (annotations: Annotations) => void;
        setQueries: (update: (prevQueries: Record<string, any>) => Record<string, any>) => void;
    };
    localeObject?: Locale;

    logger: ILogger;
    graphqlClientFactory: GraphqlClientFactory;
    Placeholder: React.FC<PlaceholderProps>;
    RootDocument: React.FC;
    widgetFactory: IWidgetFactory;
    graphQLClient: repr.IGraphQLClient;
    isSSR: boolean;
    isDocumentTypePdf?: boolean;
    exportType?: string;
}

export const defaultAnnotations = {
    items: [],
    markerCount: 0,
    markerGroupCount: {},
    formats: {},
};

export const defaultAppContext: AppContextShape = {
    isClient: false,
    data: null,
    pageName: null,
    componentName: null,
    document: null,
    documentType: 'web',
    colorPalettes: null,
    documentMetadata: { pageTitle: '', locale: 'en-US' },
    inEditMode: false,
    theme: null,
    themeSelected: '',
    fundList: {
        selectedList: [],
    },
    originalHostname: '',
    baseUrl: '/',
    url: '',
    queries: {},
    annotations: defaultAnnotations,
    annotator: new Annotator(defaultAnnotations),
    setters: {
        setSelectedList: () => {},
        setAnnotations: () => {},
        setQueries: () => {},
    },

    logger: new Logger(),
    graphqlClientFactory: new GraphqlClientFactory(),
    Placeholder: ViewerPlaceholder, // () => <div>Default placeholder, set from app provider</div>,
    RootDocument: () => <div>Default root doc, set from app provider</div>,
    widgetFactory: new NoOpWidgetFactory(),
    graphQLClient: new NoOpGraphQLClient(),

    isSSR: false,
};

export const AppContext = createContext<AppContextShape>(defaultAppContext);

export interface AppContextProviderProps extends Partial<AppContextShape> {
    children: ReactNode;
}

export const AppContextProvider: React.FC<AppContextProviderProps> = (
    props: AppContextProviderProps
) => {
    const { children, ...rest } = props;

    return (
        <AppContext.Provider value={{ ...defaultAppContext, ...rest }}>
            {children}
        </AppContext.Provider>
    );
};
